import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
    Trophy, Star, Crown, Medal, Gem, Sparkles, Target, Flame,
    Palette, Check, Lock, Zap, Pencil, Sprout
} from 'lucide-react';

const ICON_MAP = {
    pencil: Pencil,
    seedling: Sprout,
    flame: Flame,
    star: Star,
    target: Target,
    trophy: Trophy,
    crown: Crown,
    medal: Medal,
    gem: Gem,
    sparkles: Sparkles
};

const AdultRewardsPage = () => {
    const { user, api } = useAuth();
    const [rewards, setRewards] = useState(null);
    const [loading, setLoading] = useState(true);
    const [settingTheme, setSettingTheme] = useState(false);

    useEffect(() => {
        fetchRewards();
    }, []);

    const fetchRewards = async () => {
        try {
            const response = await api.get('/journal/rewards');
            setRewards(response.data);
            
            // Show notifications for newly unlocked rewards
            if (response.data.new_badges?.length > 0) {
                response.data.new_badges.forEach(badge => {
                    toast.success(`Badge Unlocked: ${badge.name}!`, {
                        description: badge.description,
                        duration: 5000
                    });
                });
            }
            if (response.data.new_themes?.length > 0) {
                response.data.new_themes.forEach(theme => {
                    toast.success(`Theme Unlocked: ${theme.name}!`, {
                        description: theme.description,
                        duration: 5000
                    });
                });
            }
        } catch (error) {
            console.error('Failed to fetch rewards:', error);
            toast.error('Failed to load rewards');
        } finally {
            setLoading(false);
        }
    };

    const handleSetTheme = async (themeId) => {
        setSettingTheme(true);
        try {
            await api.put(`/journal/theme?theme_id=${themeId}`);
            toast.success('Theme updated!');
            fetchRewards();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to set theme');
        } finally {
            setSettingTheme(false);
        }
    };

    // Redirect children to their rewards page
    if (user?.account_type === 'child') {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-96">
                    <p className="text-muted-foreground">Please visit your Rewards page from the sidebar.</p>
                </div>
            </DashboardLayout>
        );
    }

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
            </DashboardLayout>
        );
    }

    const isThemeUnlocked = (theme) => {
        return rewards?.unlocked_themes?.some(t => t.id === theme.id);
    };

    return (
        <DashboardLayout>
            <div data-testid="adult-rewards-page" className="p-4 lg:p-8 space-y-8 max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", bounce: 0.5 }}
                        className="inline-flex items-center gap-3 mb-4"
                    >
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg">
                            <Trophy className="w-8 h-8 text-white" />
                        </div>
                    </motion.div>
                    <h1 className="font-heading text-3xl font-bold mb-2">Your Rewards</h1>
                    <p className="text-muted-foreground">Keep journaling to earn badges and unlock new themes!</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="glass-card bg-gradient-to-br from-amber-500/10 to-orange-500/5">
                        <CardContent className="p-4 text-center">
                            <Zap className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                            <p className="text-3xl font-bold">{rewards?.total_points || 0}</p>
                            <p className="text-xs text-muted-foreground">Total Points</p>
                        </CardContent>
                    </Card>
                    <Card className="glass-card">
                        <CardContent className="p-4 text-center">
                            <Flame className="w-8 h-8 mx-auto mb-2 text-orange-500" />
                            <p className="text-3xl font-bold">{rewards?.streak || 0}</p>
                            <p className="text-xs text-muted-foreground">Day Streak</p>
                        </CardContent>
                    </Card>
                    <Card className="glass-card">
                        <CardContent className="p-4 text-center">
                            <Medal className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                            <p className="text-3xl font-bold">{rewards?.earned_badges?.length || 0}</p>
                            <p className="text-xs text-muted-foreground">Badges Earned</p>
                        </CardContent>
                    </Card>
                    <Card className="glass-card">
                        <CardContent className="p-4 text-center">
                            <Palette className="w-8 h-8 mx-auto mb-2 text-pink-500" />
                            <p className="text-3xl font-bold">{rewards?.unlocked_themes?.length || 0}</p>
                            <p className="text-xs text-muted-foreground">Themes Unlocked</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Next Goals */}
                <div className="grid md:grid-cols-2 gap-4">
                    {rewards?.next_badge && (
                        <Card className="glass-card border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-orange-500/10">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                                            <Target className="w-6 h-6 text-amber-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Next Badge: {rewards.next_badge.name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {rewards.next_badge.entries_needed} more {rewards.next_badge.entries_needed === 1 ? 'entry' : 'entries'} to go!
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold text-amber-500">+{rewards.next_badge.points}</p>
                                        <p className="text-xs text-muted-foreground">points</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                    
                    {rewards?.next_theme && (
                        <Card className="glass-card border-purple-500/30 bg-gradient-to-r from-purple-500/10 to-pink-500/10">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                                            <Palette className="w-6 h-6 text-purple-500" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Next Theme: {rewards.next_theme.name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {rewards.next_theme.points_needed} more points needed
                                            </p>
                                        </div>
                                    </div>
                                    <div 
                                        className="w-10 h-10 rounded-lg shadow-md"
                                        style={{ 
                                            background: `linear-gradient(135deg, ${rewards.next_theme.colors.primary} 0%, ${rewards.next_theme.colors.accent} 100%)`
                                        }}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Badges Section */}
                <div>
                    <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                        <Medal className="w-5 h-5" />
                        Badges
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {rewards?.all_badges?.map((badge, index) => {
                            const isEarned = rewards?.earned_badges?.some(b => b.name === badge.name);
                            const IconComponent = ICON_MAP[badge.icon] || Star;
                            
                            return (
                                <motion.div
                                    key={badge.name}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <Card className={`glass-card h-full ${isEarned ? 'border-primary/50' : 'opacity-50'}`}>
                                        <CardContent className="p-4 text-center">
                                            <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
                                                isEarned 
                                                    ? 'bg-gradient-to-br from-primary to-accent' 
                                                    : 'bg-muted'
                                            }`}>
                                                {isEarned ? (
                                                    <IconComponent className="w-6 h-6 text-white" />
                                                ) : (
                                                    <Lock className="w-5 h-5 text-muted-foreground" />
                                                )}
                                            </div>
                                            <p className="font-medium text-sm">{badge.name}</p>
                                            <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                                            {isEarned && (
                                                <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs">
                                                    <Check className="w-3 h-3" />
                                                    +{badge.points}
                                                </div>
                                            )}
                                            {!isEarned && (
                                                <p className="mt-2 text-xs text-muted-foreground">
                                                    {badge.days} {badge.days === 1 ? 'entry' : 'entries'}
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                {/* Themes Section */}
                <div>
                    <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                        <Palette className="w-5 h-5" />
                        Themes
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {rewards?.all_themes?.map((theme, index) => {
                            const isUnlocked = isThemeUnlocked(theme);
                            const isActive = rewards?.active_theme === theme.id;
                            
                            return (
                                <motion.div
                                    key={theme.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <Card className={`glass-card overflow-hidden ${isActive ? 'ring-2 ring-primary' : ''} ${!isUnlocked ? 'opacity-60' : ''}`}>
                                        <div 
                                            className="h-20 w-full"
                                            style={{ 
                                                background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%)`
                                            }}
                                        />
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="font-medium">{theme.name}</h3>
                                                {isActive && (
                                                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">Active</span>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground mb-3">{theme.description}</p>
                                            
                                            {isUnlocked ? (
                                                <Button 
                                                    variant={isActive ? "outline" : "default"}
                                                    size="sm" 
                                                    className="w-full"
                                                    onClick={() => handleSetTheme(theme.id)}
                                                    disabled={isActive || settingTheme}
                                                    data-testid={`theme-btn-${theme.id}`}
                                                >
                                                    {isActive ? 'Currently Active' : 'Use This Theme'}
                                                </Button>
                                            ) : (
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-muted-foreground flex items-center gap-1">
                                                        <Lock className="w-3 h-3" />
                                                        Locked
                                                    </span>
                                                    <span className="text-primary font-medium">
                                                        {theme.points_required} pts
                                                    </span>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default AdultRewardsPage;
